import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, Plus, Send, Trash2, X } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle,
  Callout, Checkbox, ConfirmModal, FormField, IconButton, Input, NativeSelect, StatusBadge, Textarea,
} from "../ui";
import apiClient from "../../utils/apiClient";
import {
  CUSTOM_PLACEHOLDERS, SLOT_OPTIONS, SMS_PROVIDERS, carriesOtp, countSlots, fitSlots, previewOtp, timeAgo,
} from "./communications";

const toForm = (s) => ({
  provider: s.provider || "smsgatewayhub",
  apiKey: "",
  apiSecret: "",
  accountSid: s.accountSid || "",
  senderId: s.senderId || "",
  entityId: s.entityId || "",
  defaultCountryCode: s.defaultCountryCode || "91",
  templateId: s.otpTemplate?.templateId || "",
  body: s.otpTemplate?.body || "",
  slots: fitSlots(s.otpTemplate?.body, s.otpTemplate?.slots),
  msg91Vars: (s.otpTemplate?.msg91Vars || []).map((v) => ({ ...v })),
  custom: { method: "POST", url: "", bodyType: "json", params: "", headers: "", body: "", successMatch: "", ...s.custom },
  dailyLimit: String(s.dailyLimit || 1000),
  enabled: !!s.enabled,
  studentLogin: !!s.otpPolicy?.studentLogin,
});

const toBody = (f) => ({
  provider: f.provider,
  apiKey: f.apiKey,
  apiSecret: f.apiSecret,
  accountSid: f.accountSid,
  senderId: f.senderId,
  entityId: f.entityId,
  defaultCountryCode: f.defaultCountryCode,
  otpTemplate: {
    templateId: f.templateId,
    body: f.body,
    slots: fitSlots(f.body, f.slots),
    msg91Vars: f.msg91Vars.filter((v) => v.name.trim()),
  },
  custom: f.custom,
  dailyLimit: Number(f.dailyLimit) || 1000,
});

// Everything a delivered test vouches for — mirrors the server's fingerprint.
const connectionKey = (f) => JSON.stringify([
  f.provider, f.apiKey, f.apiSecret, f.accountSid, f.senderId, f.entityId, f.defaultCountryCode,
  f.templateId, f.body, fitSlots(f.body, f.slots), f.msg91Vars, f.provider === "custom" ? f.custom : null,
]);

function statusOf(saved) {
  if (!saved.configured) return { status: "neutral", label: "Not set up" };
  if (!saved.enabled) return { status: "neutral", label: "Saved, switched off" };
  if (!saved.health.healthy) return { status: "danger", label: "Failing — paused" };
  if (saved.health.consecutiveFailures > 0) return { status: "warning", label: "Recent failures" };
  return { status: "success", label: "Sending through your gateway" };
}

/**
 * Settings → Email & SMS → SMS.
 *
 * The institute's own SMS gateway, and the OTP checks that need one. Every
 * provider sends the same OTP; the form asks only for what that provider
 * needs. As with email, a test SMS from the values on screen must be delivered
 * before the gateway can be switched on — the server enforces it.
 */
export default function SmsGatewaySettings({ saved, brandName, onSaved, showToast }) {
  const [form, setForm] = useState(() => toForm(saved));
  const [testPhone, setTestPhone] = useState("");
  const [testedKey, setTestedKey] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setCustom = (patch) => setForm((f) => ({ ...f, custom: { ...f.custom, ...patch } }));

  const savedKey = useMemo(() => connectionKey(toForm(saved)), [saved]);
  const key = connectionKey(form);
  const tested = testedKey === key || (saved.verified && key === savedKey);
  const status = statusOf(saved);

  const isMsg91 = form.provider === "msg91";
  const isCustom = form.provider === "custom";
  const slotCount = countSlots(form.body);
  const slots = fitSlots(form.body, form.slots);
  const otpMissing = !isMsg91 && form.body.trim() && !carriesOtp(form.body, slots);

  const setBody = (body) => set({ body, slots: fitSlots(body, form.slots) });
  const setSlot = (i, value) => set({ slots: slots.map((s, j) => (j === i ? value : s)) });
  const setVar = (i, patch) => set({ msg91Vars: form.msg91Vars.map((v, j) => (j === i ? { ...v, ...patch } : v)) });

  const sendTest = async () => {
    setBusy("test");
    setTestResult(null);
    try {
      const { data } = await apiClient.post("/institute/communications/sms/test", { ...toBody(form), phone: testPhone.trim() });
      setTestedKey(key);
      setTestResult({ tone: "success", message: data.message });
    } catch (err) {
      setTestResult({ tone: "danger", message: err?.response?.data?.message || "The test SMS could not be sent." });
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await apiClient.put("/institute/communications/sms", {
        ...toBody(form),
        enabled: form.enabled,
        otpPolicy: { studentLogin: form.enabled && form.studentLogin },
      });
      showToast?.(data.message || "SMS settings saved.");
      setForm(toForm(data.data));
      setTestedKey(null);
      onSaved?.(data.data);
    } catch (err) {
      showToast?.(err?.response?.data?.message || "Could not save SMS settings.", "error");
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    setConfirmRemove(false);
    setBusy("remove");
    try {
      const { data } = await apiClient.delete("/institute/communications/sms");
      showToast?.(data.message || "Removed.");
      const empty = { configured: false, enabled: false, verified: false, otpTemplate: {}, custom: {}, otpPolicy: {}, health: { healthy: true, consecutiveFailures: 0 } };
      setForm(toForm(empty));
      setTestResult(null);
      onSaved?.(empty);
    } catch (err) {
      showToast?.(err?.response?.data?.message || "Could not remove SMS settings.", "error");
    } finally {
      setBusy(null);
    }
  };

  const blockedSave = form.enabled && !tested;

  return (
    <AppCard>
      <AppCardHeader>
        <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-3">
          <div className="tw-min-w-0">
            <AppCardTitle>
              <span className="tw-inline-flex tw-items-center tw-gap-2">
                <MessageSquare size={18} aria-hidden="true" /> SMS
              </span>
            </AppCardTitle>
            <AppCardDescription>
              Connect your own SMS account to send OTPs under your own sender ID — and to ask for a mobile OTP at
              student login and on your enquiry forms.
            </AppCardDescription>
          </div>
          <StatusBadge status={status.status}>{status.label}</StatusBadge>
        </div>
      </AppCardHeader>

      <AppCardContent className="tw-flex tw-flex-col tw-gap-5">
        {saved.enabled && saved.health.consecutiveFailures > 0 && saved.health.lastError && (
          <Callout tone={saved.health.healthy ? "warning" : "danger"}>
            Your gateway refused a message {timeAgo(saved.health.lastFailureAt)}: {saved.health.lastError}
            {!saved.health.healthy && " Mobile OTP checks are paused — login codes go by email and enquiry forms accept submissions without a code — until it works again."}
          </Callout>
        )}

        <section className="tw-flex tw-flex-col tw-gap-4" aria-label="Gateway">
          <div className="tw-grid tw-gap-4 md:tw-grid-cols-2">
            <FormField label="SMS provider">
              <NativeSelect value={form.provider} onChange={(e) => set({ provider: e.target.value })} options={SMS_PROVIDERS} />
            </FormField>
            <FormField label="Country code" hint="For numbers typed without one. Enquiry-form codes are only sent inside this country.">
              <Input inputMode="numeric" value={form.defaultCountryCode} onChange={(e) => set({ defaultCountryCode: e.target.value })} />
            </FormField>
          </div>

          {form.provider === "smsgatewayhub" && (
            <div className="tw-grid tw-gap-4 md:tw-grid-cols-2">
              <SecretField label="API key" value={form.apiKey} saved={saved.hasApiKey} onChange={(apiKey) => set({ apiKey })} />
              <FormField label="Sender ID" required hint="The 6-letter header registered on DLT.">
                <Input value={form.senderId} onChange={(e) => set({ senderId: e.target.value })} placeholder="ACMEIN" />
              </FormField>
              <FormField label="DLT entity ID (PEID)" required>
                <Input value={form.entityId} onChange={(e) => set({ entityId: e.target.value })} />
              </FormField>
              <FormField label="DLT template ID" required hint="The ID of your approved OTP template.">
                <Input value={form.templateId} onChange={(e) => set({ templateId: e.target.value })} />
              </FormField>
            </div>
          )}

          {isMsg91 && (
            <>
              <div className="tw-grid tw-gap-4 md:tw-grid-cols-2">
                <SecretField label="Auth key" value={form.apiKey} saved={saved.hasApiKey} onChange={(apiKey) => set({ apiKey })} />
                <FormField label="MSG91 template ID" required hint="From MSG91 → SMS → Templates. MSG91 holds the DLT details.">
                  <Input value={form.templateId} onChange={(e) => set({ templateId: e.target.value })} />
                </FormField>
              </div>
              <div className="tw-flex tw-flex-col tw-gap-2" role="group" aria-labelledby="msg91-vars-title" aria-describedby="msg91-vars-hint">
                <span id="msg91-vars-title" className="tw-text-[0.8125rem] tw-font-semibold tw-text-content">Template variables</span>
                <span id="msg91-vars-hint" className="tw-text-xs tw-text-content-subtle">
                  The variable names in your MSG91 template (for ##OTP##, enter OTP), and what each one should say.
                </span>
                {form.msg91Vars.map((v, i) => (
                  <div key={i} className="tw-flex tw-items-center tw-gap-2">
                    <Input aria-label={`Variable ${i + 1} name`} value={v.name} onChange={(e) => setVar(i, { name: e.target.value })} placeholder="OTP" />
                    <NativeSelect aria-label={`Variable ${i + 1} value`} value={v.value} onChange={(e) => setVar(i, { value: e.target.value })} options={SLOT_OPTIONS} />
                    <IconButton label={`Remove variable ${i + 1}`} icon={X} variant="ghost" size="icon-sm" onClick={() => set({ msg91Vars: form.msg91Vars.filter((_, j) => j !== i) })} />
                  </div>
                ))}
                <div>
                  <AppButton variant="outline" size="sm" onClick={() => set({ msg91Vars: [...form.msg91Vars, { name: "", value: "otp" }] })}>
                    <Plus size={16} aria-hidden="true" /> Add variable
                  </AppButton>
                </div>
              </div>
            </>
          )}

          {form.provider === "twilio" && (
            <div className="tw-grid tw-gap-4 md:tw-grid-cols-3">
              <FormField label="Account SID" required>
                <Input value={form.accountSid} onChange={(e) => set({ accountSid: e.target.value })} placeholder="AC…" />
              </FormField>
              <SecretField label="Auth token" value={form.apiSecret} saved={saved.hasApiSecret} onChange={(apiSecret) => set({ apiSecret })} />
              <FormField label="From" required hint="Your Twilio number (+1…) or a Messaging Service SID (MG…).">
                <Input value={form.senderId} onChange={(e) => set({ senderId: e.target.value })} />
              </FormField>
            </div>
          )}

          {isCustom && (
            <CustomRequestFields form={form} saved={saved} set={set} setCustom={setCustom} />
          )}
        </section>

        {!isMsg91 && (
          <section className="tw-flex tw-flex-col tw-gap-3" aria-label="OTP message">
            <FormField
              label="OTP message"
              required={!isCustom}
              hint={isCustom
                ? "Sent wherever your request uses {{message}}. Paste your DLT-approved text with {#var#} where values go."
                : "Paste your DLT-approved template exactly, with {#var#} where each value goes. Any difference and operators drop the SMS."}
            >
              <Textarea rows={3} value={form.body} onChange={(e) => setBody(e.target.value)} placeholder="Your OTP for {#var#} login is {#var#}. It is valid for 10 minutes." />
            </FormField>
            {slotCount > 1 && (
              <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2 lg:tw-grid-cols-3">
                {slots.map((value, i) => (
                  <FormField key={i} label={`{#var#} number ${i + 1}`}>
                    <NativeSelect value={value} onChange={(e) => setSlot(i, e.target.value)}>
                      <option value="">Choose…</option>
                      {SLOT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </NativeSelect>
                  </FormField>
                ))}
              </div>
            )}
            {form.body.trim() && (
              <div className="tw-flex tw-flex-col tw-gap-1">
                <span className="tw-text-xs tw-font-semibold tw-text-content-muted">Students will receive</span>
                <p className="tw-m-0 tw-rounded-md tw-bg-surface-alt tw-px-3 tw-py-2 tw-text-sm tw-text-content">
                  {previewOtp(form.body, slots, brandName)}
                </p>
              </div>
            )}
            {otpMissing && <Callout tone="warning">None of the {"{#var#}"} slots is set to the OTP code, so the code would never reach the student.</Callout>}
          </section>
        )}

        <div className="tw-grid tw-gap-4 md:tw-grid-cols-2">
          <FormField
            label="Daily SMS limit"
            hint={`${saved.usageToday || 0} sent today. Caps what a scripted attack on your enquiry forms could cost you.`}
          >
            <Input type="number" inputMode="numeric" min={1} value={form.dailyLimit} onChange={(e) => set({ dailyLimit: e.target.value })} />
          </FormField>
        </div>

        <section className="tw-flex tw-flex-col tw-gap-3 tw-rounded-md tw-border tw-border-solid tw-border-line tw-p-4" aria-label="Test and switch on">
          <h4 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">1. Send a test SMS</h4>
          <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3">
            <FormField label="Mobile number" className="tw-min-w-[14rem] tw-flex-1">
              <Input type="tel" inputMode="tel" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="98765 43210" />
            </FormField>
            <AppButton variant="outline" onClick={sendTest} disabled={busy !== null || !testPhone.trim()}>
              <Send size={16} aria-hidden="true" />
              {busy === "test" ? "Sending…" : "Send test SMS"}
            </AppButton>
          </div>
          {testResult && <Callout tone={testResult.tone} role="alert">{testResult.message}</Callout>}

          <h4 className="tw-m-0 tw-mt-2 tw-text-sm tw-font-semibold tw-text-content">2. Switch it on</h4>
          <Checkbox
            checked={form.enabled}
            disabled={!form.enabled && !tested}
            onChange={(e) => set({ enabled: e.target.checked, studentLogin: e.target.checked && form.studentLogin })}
            label="Send OTPs through this gateway"
            description={tested
              ? "Login, enquiry-form, exam and two-factor codes for your institute."
              : "Send a test SMS with these settings first, and check it arrived with the code in it."}
          />
          {blockedSave && (
            <Callout tone="warning">
              These settings have not delivered a test yet. Send a test SMS, or untick the box to save them switched off.
            </Callout>
          )}
        </section>

        <section className="tw-flex tw-flex-col tw-gap-3" aria-label="Mobile OTP checks">
          <h4 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">Mobile OTP checks</h4>
          <Checkbox
            checked={form.studentLogin}
            disabled={!form.enabled}
            onChange={(e) => set({ studentLogin: e.target.checked })}
            label="Students enter an OTP when they log in"
            description="After the password or Google sign-in, students type a code sent to their mobile — on the website and in the mobile app. Students with no mobile number, or while your gateway is failing, get the code by email instead, so nobody is locked out."
          />
          <p className="tw-m-0 tw-text-sm tw-text-content-muted">
            Enquiry forms: switch on <strong>Verify mobile number with OTP</strong> for each form in{" "}
            <Link to="/enquiry-forms">Enquiry Forms</Link>.
          </p>
        </section>

        <div className="tw-flex tw-flex-wrap tw-gap-2">
          <AppButton onClick={save} disabled={busy !== null || blockedSave}>
            {busy === "save" ? "Saving…" : "Save SMS settings"}
          </AppButton>
          {saved.configured && (
            <AppButton variant="outline-destructive" onClick={() => setConfirmRemove(true)} disabled={busy !== null}>
              <Trash2 size={16} aria-hidden="true" />
              Remove
            </AppButton>
          )}
        </div>
      </AppCardContent>

      <ConfirmModal
        show={confirmRemove}
        title="Remove your SMS gateway?"
        message="OTPs go through the platform's sender again, and the student-login OTP and enquiry-form OTP checks stop. Saved credentials are deleted."
        confirmText="Remove"
        variant="danger"
        onConfirm={remove}
        onHide={() => setConfirmRemove(false)}
      />
    </AppCard>
  );
}

/** A write-only credential: the saved one is never shown, blank keeps it. */
function SecretField({ label, value, saved, onChange }) {
  return (
    <FormField label={label} required={!saved} hint={saved ? "Saved and encrypted. Leave blank to keep it." : "Stored encrypted."}>
      <Input type="password" value={value} onChange={(e) => onChange(e.target.value)} placeholder={saved ? "••••••••  (saved)" : ""} autoComplete="new-password" />
    </FormField>
  );
}

/** Any HTTP SMS API — an in-house platform included — described as templates. */
function CustomRequestFields({ form, saved, set, setCustom }) {
  const c = form.custom;
  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <div className="tw-grid tw-gap-4 md:tw-grid-cols-[8rem_1fr_10rem]">
        <FormField label="Method">
          <NativeSelect value={c.method} onChange={(e) => setCustom({ method: e.target.value })} options={[{ value: "POST", label: "POST" }, { value: "GET", label: "GET" }]} />
        </FormField>
        <FormField label="API URL" required>
          <Input value={c.url} onChange={(e) => setCustom({ url: e.target.value })} placeholder="https://sms.yourprovider.com/api/send" />
        </FormField>
        {c.method === "POST" && (
          <FormField label="Body format">
            <NativeSelect value={c.bodyType} onChange={(e) => setCustom({ bodyType: e.target.value })} options={[{ value: "json", label: "JSON" }, { value: "form", label: "Form fields" }]} />
          </FormField>
        )}
      </div>
      <div className="tw-grid tw-gap-4 md:tw-grid-cols-2">
        <FormField label="Query parameters" hint='A JSON object, e.g. {"to": "{{mobile}}", "text": "{{message}}"}'>
          <Textarea rows={4} value={c.params} onChange={(e) => setCustom({ params: e.target.value })} spellCheck={false} />
        </FormField>
        <FormField label="Headers" hint='A JSON object, e.g. {"Authorization": "Bearer {{apiKey}}"}'>
          <Textarea rows={4} value={c.headers} onChange={(e) => setCustom({ headers: e.target.value })} spellCheck={false} />
        </FormField>
        {c.method === "POST" && (
          <FormField label="Body" hint="A JSON object. Sent as JSON or as form fields, per the format above.">
            <Textarea rows={4} value={c.body} onChange={(e) => setCustom({ body: e.target.value })} spellCheck={false} />
          </FormField>
        )}
        <FormField label="Success text" hint='Optional. Text the reply must contain to count as sent, e.g. "success". Needed when the API answers 200 even to a refusal.'>
          <Input value={c.successMatch} onChange={(e) => setCustom({ successMatch: e.target.value })} />
        </FormField>
      </div>
      <div className="tw-grid tw-gap-4 md:tw-grid-cols-2">
        <SecretField label="API key" value={form.apiKey} saved={saved.hasApiKey} onChange={(apiKey) => set({ apiKey })} />
        <SecretField label="API secret (optional)" value={form.apiSecret} saved={saved.hasApiSecret} onChange={(apiSecret) => set({ apiSecret })} />
        <FormField label="Sender ID" hint="Optional — available as {{senderId}}.">
          <Input value={form.senderId} onChange={(e) => set({ senderId: e.target.value })} />
        </FormField>
        <div className="tw-grid tw-grid-cols-2 tw-gap-4">
          <FormField label="DLT entity ID" hint="{{entityId}}">
            <Input value={form.entityId} onChange={(e) => set({ entityId: e.target.value })} />
          </FormField>
          <FormField label="DLT template ID" hint="{{templateId}}">
            <Input value={form.templateId} onChange={(e) => set({ templateId: e.target.value })} />
          </FormField>
        </div>
      </div>
      <div className="tw-rounded-md tw-bg-surface-alt tw-px-3 tw-py-2">
        <span className="tw-text-xs tw-font-semibold tw-text-content-muted">Placeholders you can use</span>
        <dl className="tw-m-0 tw-mt-1 tw-grid tw-gap-x-4 tw-gap-y-0.5 tw-text-xs sm:tw-grid-cols-2">
          {CUSTOM_PLACEHOLDERS.map(([token, meaning]) => (
            <div key={token} className="tw-flex tw-gap-2">
              <dt className="tw-font-mono tw-text-content">{token}</dt>
              <dd className="tw-m-0 tw-text-content-muted">{meaning}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}
